/**
 * Keep the app shell — and the iconbar with the touch-only indent/outdent controls at its top —
 * pinned to the VISUAL viewport while the mobile soft keyboard is up.
 *
 * Android honors the viewport meta's `interactive-widget=resizes-content`: the keyboard shrinks the
 * LAYOUT viewport, `100dvh` follows, and the pinned header stays visible — this fix never engages.
 * iOS WKWebView (and iOS Safari) ignores that directive: the keyboard shrinks only the VISUAL
 * viewport, and WebKit pans the page to reveal the caret — dragging the header above the visible
 * area, so the indent/outdent buttons become unreachable exactly when they're needed.
 *
 * Fix: while the visual viewport is meaningfully shorter than the layout viewport (keyboard up),
 * mirror the visual viewport into CSS vars + a `data-vv-keyboard` flag on <html>. styles.css sizes
 * `.lapo-shell` to the visual height and translates it down by WebKit's pan offset, so the shell's
 * top edge — the iconbar — tracks the visible top. Cancelling the pan also undoes WebKit's caret
 * reveal, so after each resize we scroll the caret back into view inside `.lapo-main-scroll` (the
 * shell's own scroller, which now ends above the keyboard).
 */

/** Layout-vs-visual height gap (px) that counts as "keyboard up" — well below a real keyboard
 * (~260px+) and well above URL-bar/safe-area jitter on the mobile web build. */
const KEYBOARD_MIN_PX = 80;

/** Clearance (px) kept between the caret and the keyboard when re-revealing it. */
const CARET_MARGIN = 24;

/** The caret's client rect, from the live selection. A collapsed caret often reports an empty
 * bounding rect, so fall back to the range's rect list, then the focused node's element. */
function caretRect(): DOMRect | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  if (rect.width || rect.height || rect.top || rect.left) return rect;
  const rects = range.getClientRects();
  if (rects.length > 0) return rects[0]!;
  const node = sel.focusNode;
  const el = node instanceof Element ? node : (node?.parentElement ?? null);
  return el ? el.getBoundingClientRect() : null;
}

/** Scroll the caret back above the keyboard inside the main scroller. In client coordinates the
 * visible band (after the shell transform) spans offsetTop … offsetTop + height. */
function revealCaretInScroller(vv: VisualViewport): void {
  const scroller = document.querySelector('.lapo-main-scroll');
  if (!(scroller instanceof HTMLElement)) return;
  const active = document.activeElement;
  if (!active || !scroller.contains(active)) return;
  const rect = caretRect();
  if (!rect) return;
  const visibleBottom = vv.offsetTop + vv.height;
  const overflow = rect.bottom - (visibleBottom - CARET_MARGIN);
  if (overflow > 0) scroller.scrollTop += overflow;
}

/**
 * Start mirroring the visual viewport (see module doc). Returns a teardown that removes the
 * listeners and clears any applied state. No-op (and a no-op teardown) where `visualViewport` is
 * unavailable.
 */
export function installKeyboardViewportFix(): () => void {
  const vv = window.visualViewport;
  if (!vv) return () => {};
  const html = document.documentElement;
  let engaged = false;

  const release = (): void => {
    if (!engaged) return;
    engaged = false;
    delete html.dataset.vvKeyboard;
    html.style.removeProperty('--lapo-vv-height');
    html.style.removeProperty('--lapo-vv-top');
  };

  const apply = (revealCaret: boolean): void => {
    // scale ≈ 1 only: during pinch-zoom the visual viewport is also short, and fighting the user's
    // pan with a transform would break zooming. clientHeight is the LAYOUT viewport height, which
    // the iOS keyboard never changes (unlike window.innerHeight, which tracks the visual viewport).
    const keyboardUp =
      Math.abs(vv.scale - 1) < 0.05 && html.clientHeight - vv.height > KEYBOARD_MIN_PX;
    if (!keyboardUp) {
      release();
      return;
    }
    engaged = true;
    html.dataset.vvKeyboard = 'true';
    html.style.setProperty('--lapo-vv-height', `${vv.height}px`);
    // pageTop = layout scroll + visual pan: translating the shell by it puts its top edge (the
    // iconbar) at the visible top whichever way WebKit moved the page.
    html.style.setProperty('--lapo-vv-top', `${vv.pageTop}px`);
    if (revealCaret) revealCaretInScroller(vv);
  };

  // resize = keyboard show/hide/height change (re-reveal the caret we just un-panned); scroll = the
  // pan offset moved (keyboard animation frames, or the user dragging with the keyboard up).
  const onResize = (): void => apply(true);
  const onScroll = (): void => apply(false);
  vv.addEventListener('resize', onResize);
  vv.addEventListener('scroll', onScroll);
  apply(false);

  return () => {
    vv.removeEventListener('resize', onResize);
    vv.removeEventListener('scroll', onScroll);
    release();
  };
}
